import type { HealthStatus } from '../types';

/**
 * Small typed client for the ITA6 Worker API.
 *
 * All requests send cookies (`credentials: 'include'`) because authentication
 * uses HttpOnly cookies rather than tokens kept in JavaScript.
 */

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/+$/, '');

interface SuccessEnvelope<T> {
  success: true;
  data: T;
}

interface ErrorEnvelope {
  success: false;
  error: {
    code: string;
    message: string;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

function isSuccess<T>(payload: unknown): payload is SuccessEnvelope<T> {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { success?: unknown }).success === true &&
    'data' in (payload as Record<string, unknown>)
  );
}

function isError(payload: unknown): payload is ErrorEnvelope {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { success?: unknown }).success === false &&
    typeof (payload as { error?: unknown }).error === 'object'
  );
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'include',
      headers:
        body === undefined && !options?.headers
          ? undefined
          : { ...(body === undefined ? {} : { 'Content-Type': 'application/json' }), ...options?.headers },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Could not reach the ITA6 API. Is the Worker running?');
  }

  const raw = await response.text();
  let payload: unknown = null;
  if (raw) {
    try {
      payload = JSON.parse(raw);
    } catch {
      payload = null;
    }
  }

  if (!response.ok || !isSuccess<T>(payload)) {
    const error = isError(payload) ? payload.error : null;
    throw new ApiError(
      response.status,
      error?.code ?? 'REQUEST_FAILED',
      error?.message ?? `Request failed with status ${response.status}.`,
    );
  }

  return payload.data;
}

/** Extra request headers (e.g. `Idempotency-Key`). */
export interface RequestOptions {
  headers?: Record<string, string>;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, undefined, options),
};

/**
 * `GET /api/health` intentionally returns a flat payload (not the usual
 * envelope), so it is fetched directly rather than through `request()`.
 */
export async function fetchHealth(): Promise<HealthStatus> {
  const response = await fetch(`${API_BASE}/health`, { credentials: 'include' });
  const payload = (await response.json().catch(() => null)) as HealthStatus | null;
  if (!response.ok || !payload || typeof payload.ok !== 'boolean') {
    throw new ApiError(response.status || 0, 'HEALTH_UNAVAILABLE', 'The API health check failed.');
  }
  return payload;
}
